import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Wordmark — the Cooori brand lockup, in every app (ADR-0088 D2): the real
 * wordmark and a "Speaking Coach" sublabel separated by a 1px hairline, or,
 * for a narrow rail, the standalone Cooori O.
 *
 * The SVGs are byte-identical copies of the approved source exports in
 * `assets/brand/cooori/derived`, owned here for runtime bundling. No app reaches
 * into another's `public/` folder for them.
 * The product never draws the wordmark below 18 px of height, the default
 * (Product Brand Guide §3, which waives the corporate 100 px width minimum
 * for product UI).
 *
 * Accessible name: the mark is an image named "Cooori"; the sublabel is
 * visible text, so the lockup reads "Cooori Speaking Coach".
 */
import { createContext, useContext } from 'react';
import coooriO from './assets/cooori-o.svg';
import coooriWordmark from './assets/cooori-wordmark.svg';
import coooriWordmarkReversed from './assets/cooori-wordmark-reversed.svg';
import coooriWordmarkWhite from './assets/cooori-wordmark-white.svg';
import coooriOWhite from './assets/cooori-o-white.svg';
const wordmarkSources = {
    standard: coooriWordmark,
    reversed: coooriWordmarkReversed,
    white: coooriWordmarkWhite,
};
const WordmarkToneContext = createContext('standard');
/** Explicit artwork choice for a surrounding surface; never detects or enables
 * a theme. A Wordmark's own tone overrides the surrounding choice. */
export function WordmarkToneProvider({ tone, children }) {
    return _jsx(WordmarkToneContext, { value: tone, children: children });
}
export function Wordmark({ height = 18, compact = false, sublabel = true, tone: selectedTone, className, }) {
    const surroundingTone = useContext(WordmarkToneContext);
    const tone = selectedTone ?? surroundingTone;
    if (process.env.NODE_ENV !== 'production' && !compact && height < 18) {
        // eslint-disable-next-line no-console -- Development layout diagnostic; production builds remove this branch.
        console.warn('Wordmark requires at least 18 px of height; reserve that space in the parent layout.');
    }
    const wordmarkHeight = Math.max(18, height);
    if (compact) {
        return (_jsx("img", { src: tone === 'white' ? coooriOWhite : coooriO, alt: "Cooori", "data-slot": "wordmark", "data-tone": tone, className: className, style: { height, width: height, display: 'block' } }));
    }
    return (_jsxs("div", { "data-slot": "wordmark", "data-tone": tone, className: ['sc-wordmark', className].filter(Boolean).join(' '), style: {
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            flex: '0 0 auto',
            whiteSpace: 'nowrap',
        }, children: [_jsx("img", { src: wordmarkSources[tone], alt: "Cooori", style: { height: wordmarkHeight, width: 'auto', display: 'block' } }), sublabel && (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", style: {
                            width: 1, height: 17,
                            background: tone === 'standard' ? 'var(--color-border-default)' : 'var(--color-white)',
                            opacity: tone === 'standard' ? undefined : 0.35,
                        } }), _jsx("span", { className: "sub", style: {
                            fontSize: 14.5,
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            color: tone === 'standard' ? 'var(--color-text-secondary)' : 'var(--color-white)',
                        }, children: "Speaking Coach" })] }))] }));
}
//# sourceMappingURL=brand.js.map